import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import { InputGroup, Form, Tab, Button, Modal } from "react-bootstrap";
import { useDataFetch } from "./../../hooks/useDataFetch";
import { useDegrees } from "./../../hooks/useDegrees";

import { useNavigate } from "react-router-dom";

const months = {
  1: "Enero",
  2: "Febrero",
  3: "Marzo",
  4: "Abril",
  5: "Mayo",
  6: "Junio",
  7: "Julio",
  8: "Agosto",
  9: "Septiembre",
  10: "Octubre",
  11: "Noviembre",
  12: "Diciembre",
};

const fuzzyFilter = (row, columnId, value) => {
  return rankItem(row.getValue(columnId), value).passed;
};

const GlobalFilter = ({ globalFilter, setGlobalFilter }) => {
  return (
    <InputGroup className="mb-3">
      <Form.Control
        placeholder="Buscar"
        value={globalFilter ?? ""}
        onChange={(e) => setGlobalFilter(e.target.value || undefined)}
      />
    </InputGroup>
  );
};

const Nonpay = () => {
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [yearFilter, setYearFilter] = useState(
    new Date().getFullYear().toString()
  );
  const [degreeFilter, setDegreeFilter] = useState();
  const [conceptFilter, setConceptFilter] = useState("Mensualidad");
  const [monthFilter, setMonthFilter] = useState((new Date().getMonth() + 1).toString());

  const [newStudent, setNewStudent] = useState(false);
  const [configStudent, setConfiStudent] = useState(false);
  const [studentId, setStudentId] = useState();

  const { getData, invalidateQuery } = useDataFetch();

  const { status, data, error, isFetching, refetch } = getData(
    ["nonpaying-student"],
    "/student/nonpaying-student",
    { enabled: false },
    { concept: conceptFilter, year: yearFilter, degree_id: degreeFilter, month: monthFilter }
  );

  const { degrees: datadegreeList } = useDegrees(yearFilter);

  useEffect(() => {
    setDegreeFilter("");
  }, [yearFilter]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "identification",
        header: "Identificación",
      },
      {
        accessorFn: (row) => `${row.first_name} ${row.last_name}`,
        id: "full_name",
        header: "Nombre",
      },
      {
        accessorFn: (row) => `${row?.level || "-"} ${row?.degree || "-"}`,
        id: "grade",
        header: "Grado",
      },
      {
        accessorFn: (row) => row.month ? months[row.month] : "-",
        id: "month",
        header: "Mes",
      },
      {
        id: "options",
        header: "",
        cell: ({ row }) => (
          <div className="d-flex gap-10">
            <i
              className="flaticon-381-id-card-1"
              style={{ fontSize: 20, cursor: "pointer", marginLeft: 10 }}
              onClick={() => {
                navigate(`/settlement/${row.original.student_id}/${yearFilter}`);
              }}
            ></i>
          </div>
        ),
      },
    ],
    [yearFilter]
  );

  const tableData = useMemo(() => data?.students ?? [], [data?.students]);

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      globalFilter,
      pagination,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter, // Aplicar filtro fuzzy para el filtro global
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
  });

  return (
    <Tab.Container defaultActiveKey="all">
      <div className="d-flex justify-content-between">
        <div className="d-flex">
          <select
            className="form-control mb-3"
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
          >
            {[2025, 2026, 2027, 2028, 2029, 2030].map((year) => (
              <option
                key={year}
                value={year.toString()}
              >
                {year}
              </option>
            ))}
          </select>

          <select
            className="form-control mb-3 ml-3"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
          >
            {Object.entries(months).map((month) => (
              <option
                key={month[0]}
                value={month[0]}
              >
                {month[1]}
              </option>
            ))}
          </select>

          <select
            className="form-control mb-3 ml-3"
            value={conceptFilter}
            onChange={(e) => setConceptFilter(e.target.value)}
          >
            <option value="Mensualidad">Mensualidad</option>
            <option value="Matricula">Matrícula</option>
          </select>

          <select
            style={{ width: 200 }}
            className="form-control mb-3 ml-3"
            value={degreeFilter || ""}
            onChange={(e) => setDegreeFilter(e.target.value)}
          >
            <option value="">Todos los grados</option>
            {datadegreeList?.map((e) => (
              <option key={e.id} value={e.id}>
                {e.level} {e.degree}
              </option>
            ))}
          </select>

          <Button
            className="btn btn-primary text-nowrap mb-3 ml-3"
            disabled={isFetching}
            onClick={() => {
              invalidateQuery("nonpaying-student");
              refetch();
            }}
          >
            <i className="fa fa-search scale5 mr-3" aria-hidden="true" />
            {isFetching ? "Buscando..." : "Buscar"}
          </Button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table card-table display dataTablesCard dataTable no-footer">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
          <div className="dataTables_info">
            Mostrando{" "}
            {table.getRowModel().rows.length *
              (table.getState().pagination.pageIndex + 1)}{" "}
            de {table.getRowCount().toLocaleString()} registros
          </div>
          <div
            className="dataTables_paginate paging_simple_numbers"
            id="example2_paginate"
          >
            <Button
              className="paginate_button previous"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Anterior
            </Button>

            <Button
              className="paginate_button next"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Siguiente
            </Button>
          </div>
        </div>
      </div>
    </Tab.Container>
  );
};

export default Nonpay;
